import { api } from '../utils/api';
import WebSocketService from './websocketService';
import CallService from './callService';
import NotificationService from './notificationService';
import { getAuthToken } from '../utils/auth';

interface EmergencyData {
  victimId: string;
  timestamp: number;
  location?: {
    latitude: number;
    longitude: number;
    accuracy: number;
    address?: string;
  };
  status: 'active' | 'resolved';
  contactsNotified: Array<{
    id: string;
    name: string;
    relationship: string;
    notified: boolean;
  }>;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  photo: string;
  school?: string;
  grade?: string;
  role?: string;
}

interface Contact {
  id: string;
  name: string;
  relationship: string;
  phone?: string;
  email?: string;
}

class EmergencyService {
  private static instance: EmergencyService;
  private wsService: WebSocketService | null = null;
  private callService: CallService | null = null;
  private notificationService: any | null = null;
  private locationWatchId: number | null = null;

  private constructor() {
    // Don't initialize services in constructor to avoid circular dependencies
  }

  private getWebSocketService(): WebSocketService {
    try {
      if (!this.wsService) {
        this.wsService = WebSocketService.getInstance();
      }
      return this.wsService;
    } catch (error) {
      console.error('[Emergency] Error getting WebSocket service:', error);
      throw new Error('WebSocket service not available');
    }
  }

  private getCallService(): CallService {
    try {
      if (!this.callService) {
        this.callService = CallService.getInstance();
      }
      return this.callService;
    } catch (error) {
      console.error('[Emergency] Error getting Call service:', error);
      throw new Error('Call service not available');
    }
  }

  private getNotificationService(): any {
    try {
      if (!this.notificationService) {
        this.notificationService = NotificationService;
      }
      return this.notificationService;
    } catch (error) {
      console.error('[Emergency] Error getting Notification service:', error);
      throw new Error('Notification service not available');
    }
  }

  public static getInstance(): EmergencyService {
    if (!EmergencyService.instance) {
      EmergencyService.instance = new EmergencyService();
    }
    return EmergencyService.instance;
  }

  private async ensureAuthenticated(): Promise<void> {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  public async autoLogin(userData: UserData): Promise<void> {
    try {
      await api.post('/api/auth/auto-login', { userData });
    } catch (error) {
      console.error('[Emergency] Error during auto-login:', error);
      throw error;
    }
  }

  public async triggerEmergency(
    location: { latitude: number; longitude: number; accuracy: number },
    highPriority: boolean = false
  ): Promise<void> {
    try {
      await this.ensureAuthenticated();

      // Pause all other processes
      await this.pauseOtherProcesses();

      // Get user's emergency contacts
      const contacts = await this.getEmergencyContacts();

      // Trigger emergency with high priority
      await api.post('/emergency/trigger', { location, highPriority });

      // Broadcast emergency to all contacts
      await this.broadcastEmergency(contacts, location);

      // Start real-time location tracking
      this.startLocationTracking();
    } catch (error) {
      console.error('[Emergency] Error triggering emergency:', error);
      throw error;
    }
  }

  private async pauseOtherProcesses(): Promise<void> {
    try {
      await api.post('/system/pause-processes');
    } catch (error) {
      console.error('[Emergency] Error pausing processes:', error);
      throw error;
    }
  }

  public async getEmergencyContacts(): Promise<Contact[]> {
    try {
      const response = await api.get('/api/emergency/contacts');
      return response.data;
    } catch (error) {
      console.error('[Emergency] Error getting emergency contacts:', error);
      // Return empty array on error to prevent app crashes
      return [];
    }
  }

  public async broadcastEmergency(contacts: Contact[], location: { latitude: number; longitude: number; accuracy: number }): Promise<void> {
    try {
      await this.ensureAuthenticated();

      // Send emergency notifications
      await this.getNotificationService().sendEmergencyNotifications(contacts);

      // Initiate emergency calls
      await this.getCallService().initiateEmergencyCalls(contacts);

      // Broadcast via WebSocket
      await this.getWebSocketService().broadcastEmergency(contacts);
    } catch (error) {
      console.error('[Emergency] Error broadcasting emergency:', error);
      throw error;
    }
  }

  private startLocationTracking(): void {
    // Start real-time location tracking
    this.locationWatchId = navigator.geolocation.watchPosition(
      async (position) => {
        try {
          await this.ensureAuthenticated();
          await api.post('/api/emergency/update-location', {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        } catch (error) {
          console.error('[Emergency] Error updating location:', error);
        }
      },
      (error) => {
        console.error('[Emergency] Error tracking location:', error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 5000
      }
    );
  }

  public async resolveEmergency(): Promise<void> {
    try {
      await this.ensureAuthenticated();
      await api.post('/api/emergency/resolve');
      
      // Stop location tracking if active
      if (this.locationWatchId !== null) {
        navigator.geolocation.clearWatch(this.locationWatchId);
        this.locationWatchId = null;
      }

      // Resume normal processes
      await api.post('/api/system/resume-processes');

      // Notify all contacts that emergency is resolved
      await this.notifyEmergencyResolved();
    } catch (error) {
      console.error('[Emergency] Error resolving emergency:', error);
      throw error;
    }
  }

  private async notifyEmergencyResolved(): Promise<void> {
    try {
      await this.ensureAuthenticated();
      const contacts = await this.getEmergencyContacts();
      await this.getNotificationService().sendEmergencyResolvedNotifications(contacts);
      await this.getWebSocketService().broadcastEmergencyResolved(contacts);
    } catch (error) {
      console.error('[Emergency] Error notifying emergency resolved:', error);
    }
  }

  public async isEmergencyActive(): Promise<boolean> {
    try {
      await this.ensureAuthenticated();
      const response = await api.get('/api/emergency/status');
      return response.data.isActive;
    } catch (error) {
      console.error('[Emergency] Error checking emergency status:', error);
      // Return false on error to prevent app crashes
      return false;
    }
  }
}

export default EmergencyService.getInstance(); 